import { useCallback } from "react";
import { useSetAtom } from "jotai";
import { useApi } from "./useApi";
import type { SurchargeQuoteResponse } from "@models/paymentQuote";
import { isFetchingSurchargeQuoteAtom } from "@/store/paymentQuoteAtoms";

const SURCHARGE_QUOTE_ENDPOINT = "/payments/card-surcharge";

export const usePaymentQuoteApi = () => {
    const { callApi } = useApi();
    const setIsFetching = useSetAtom(isFetchingSurchargeQuoteAtom);

    // Card-surcharge quote for the checkout totals (#297). CONFIRMED against API main @
    // 386f687 (PR 242) — Features/Payments/CalculateCardSurcharge, an anonymous
    // display-only endpoint; the server re-computes the fee authoritatively at placement.
    // `amount` is the DISPLAYED subtotal + shipping; the server owns both the rate and
    // the fee arithmetic (§1.11) — the UI only renders what comes back.
    //
    // The server binds `amount` as a STRICT invariant-culture decimal and 400s
    // garbage/missing/zero/negative: toFixed(2) pins the "123.45" shape (never locale
    // separators or exponents), and callers must gate on amount > 0 — Checkout's quote
    // effect never fires this for a zero amount.
    const getSurchargeQuote = useCallback(
        async (amount: number): Promise<SurchargeQuoteResponse> => {
            setIsFetching(true);

            try {
                const params = new URLSearchParams({
                    amount: amount.toFixed(2),
                });
                return await callApi<SurchargeQuoteResponse>(
                    `${SURCHARGE_QUOTE_ENDPOINT}?${params.toString()}`,
                    // The caller (Checkout) owns the failure UI: it renders an inline
                    // "we couldn't calculate the fee" alert with a Retry. A global toast
                    // on top of that would double-report one failure — and this quote
                    // re-fires on every amount change, so a persistently failing endpoint
                    // would stack toasts over a page the shopper is still working in.
                    { skipErrorNotification: true },
                );
            } finally {
                setIsFetching(false);
            }
        },
        [callApi, setIsFetching],
    );

    return {
        getSurchargeQuote,
    };
};
